import React, { useState, useEffect } from 'react';
import { LogOut, Settings, User } from 'lucide-react';
import toast from 'react-hot-toast';

export function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    // Get email from JWT token
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (token) {
      try {
        // Decode JWT payload (middle part)
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserEmail(payload.email || null);
      } catch (error) {
        console.error('Error decoding token:', error);
        setUserEmail(null);
      }
    }
  }, []);

  const handleSignOut = async () => {
    try {
      setLoading(true);
      // Clear tokens
      localStorage.removeItem('token');
      localStorage.removeItem('auth_token');
      // Trigger storage event for other tabs/components
      window.dispatchEvent(new Event('storage'));
      
      toast.success('تم تسجيل الخروج بنجاح! 👋');
      
      // Reload to go back to login
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('حدث خطأ أثناء تسجيل الخروج');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    try {
      setLoading(true);
      setError(null);

      // TODO: Implement password change via API
      console.log('Password change not yet implemented');
      alert('تحديث كلمة المرور غير متاح حالياً');
      
      setShowPasswordModal(false);
      setNewPassword('');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'حدث خطأ أثناء تغيير كلمة المرور');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* User Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '7px 12px', borderRadius: 10,
          background: isOpen ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.05)',
          border: `1px solid ${isOpen ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.08)'}`,
          color: 'var(--text-primary)', cursor: 'pointer',
          transition: 'all 0.18s',
        }}
        onMouseEnter={e => {
          if (!isOpen) {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.08)';
          }
        }}
        onMouseLeave={e => {
          if (!isOpen) {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.05)';
          }
        }}
      >
        <div style={{
          width: 28, height: 28, borderRadius: 8,
          background: 'linear-gradient(135deg,#6366F1,#8B5CF6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <User style={{ width: 14, height: 14, color: 'white' }} />
        </div>
        <span style={{ fontSize: 13, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {userEmail || '...'}
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', left: 0,
          minWidth: 200,
          background: 'rgba(13,18,33,0.98)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 12,
          padding: '6px',
          zIndex: 200,
          boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
        }}>
          <button
            onClick={() => { setShowPasswordModal(true); setIsOpen(false); }}
            disabled={loading}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              padding: '9px 12px', borderRadius: 8, border: 'none', background: 'transparent',
              color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)';
            }}
          >
            <Settings style={{ width: 14, height: 14 }} />
            <span>تغيير كلمة المرور</span>
          </button>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', margin: '4px 0' }} />

          <button
            onClick={handleSignOut}
            disabled={loading}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              padding: '9px 12px', borderRadius: 8, border: 'none', background: 'transparent',
              color: '#FCA5A5', cursor: 'pointer', fontSize: 13,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239,68,68,0.1)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            <LogOut style={{ width: 14, height: 14 }} />
            <span>تسجيل الخروج</span>
          </button>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', zIndex: 500,
        }}>
          <div style={{
            background: 'var(--bg-elevated)', border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 16, padding: 32, width: 380,
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, color: 'var(--text-primary)' }}>
              تغيير كلمة المرور
            </h3>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="كلمة المرور الجديدة"
              className="input-field"
              style={{ marginBottom: 16 }}
              dir="rtl"
            />
            {error && (
              <div style={{ fontSize: 12, color: '#FCA5A5', marginBottom: 12 }}>{error}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8 }}>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="btn-ghost"
                style={{ fontSize: 13 }}
                disabled={loading}
              >إلغاء</button>
              <button
                onClick={handlePasswordChange}
                className="btn-primary"
                style={{ fontSize: 13 }}
                disabled={loading || !newPassword}
              >
                {loading ? 'جاري التحديث...' : 'تحديث'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}